import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "../../Component/Common/Table";
import Button from "../../Component/Common/Button";
import AddIcon from "@mui/icons-material/Add";
import ToggleSwitch from "../../Component/Common/ToggleSwitch";
import Pagination from "../../Component/Common/Pagination";
import {
  getOrgCourses,
  deleteOrgCourse,
  selectCourse,
  toggleOrgPublishStatus,
} from "../../store/course/course.action";
import { handleOpenNew } from "../../util/Dialogue";
import { ReactComponent as TrashIcon } from "../../assets/icons/trashIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/EditBtn.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/EyesIcon.svg";
import noImageFrom from "../../assets/images/noimage.png";
import { warning } from "../../util/Alert";
import CreateCourse from "../../Component/dialogue/CreateCourse";
import CourseDetail from "../Courses/CourseDetail";
import dayjs from "dayjs";
import PageContainer from "../../Component/Common/Page";
import DataActions from "../../Component/Common/DataActions";

const OrgCourses = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.loggedUser);
  const { dialogueType } = useSelector((state) => state.dialogue);
  const { courseData, totalCourses, selectedCourse } = useSelector(
    (state) => state.course,
  );

  const [editingCourseData, setEditingCourseData] = useState(null);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [debounceSearchText, setDebounceSearchText] = useState("");

  useEffect(() => {
    dispatch(getOrgCourses(page, size, debounceSearchText));
  }, [dispatch, page, size, debounceSearchText]);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleRowsPerPage = (value) => {
    setPage(1);
    setSize(value);
  };

  const handleDeleteCourse = (course) => {
    const data = warning();
    data
      .then((res) => {
        if (res) {
          const yes = res.isConfirmed;
          if (yes) {
            dispatch(deleteOrgCourse(course._id));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleEditSuccess = () => {
    setEditingCourseData(null);
  };

  const handleDeselectCourse = () => {
    dispatch(selectCourse(null));
  };

  const handleTogglePublish = (row) => {
    const id = row?._id;
    dispatch(toggleOrgPublishStatus(id));
  };

  const orgCourseMapData = [
    {
      Header: "Unique Id",
      Cell: ({ row }) => <p className="fw-bold mb-0">{row?.uniqueId}</p>,
    },
    {
      Header: "Title",
      Cell: ({ row }) => <p className="fw-bold mb-0">{row?.title}</p>,
    },
    {
      Header: "Users Enrolled",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.usersEnrolled ? row?.usersEnrolled : 0}
        </span>
      ),
    },
    {
      Header: "Completed By",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.courseCompletedByNumber ? row?.courseCompletedByNumber : 0}
        </span>
      ),
    },
    {
      Header: "Certified to",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.userReceivedCertificate ? row?.userReceivedCertificate : 0}
        </span>
      ),
    },
    // {
    //   Header: "Rating",
    //   Cell: ({ row }) => (
    //     <span className="text-capitalize">
    //       {row?.rating ? row?.rating : "-"}
    //     </span>
    //   ),
    // },
    {
      Header: "Duration",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.duration ? `${row?.duration} Min` : "-"}
        </span>
      ),
    },
    {
      Header: "Language",
      // body: "languages",
      Cell: ({ row }) => (
        <div
          className="d-flex text-start flex-wrap gap-1 px-2"
          style={{ maxWidth: "200px" }}
        >
          {row?.language}
        </div>
      ),
    },
    {
      Header: "Difficulty",
      // body: "difficulty",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.difficulty ? row?.difficulty : "-"}
        </span>
      ),
    },
    {
      Header: "Thumbnail",
      Cell: ({ row }) => (
        <img
          src={row?.thumbnail}
          style={{
            objectFit: "cover",
          }}
          alt="Course thumbnail"
          height={50}
          width={50}
          onError={(e) => (e.target.src = noImageFrom)}
        />
      ),
    },
    {
      Header: "Certification",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row.enableCertification ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    // {
    //   Header: "Price",
    //   Cell: ({ row }) => (
    //     <span className="text-capitalize">
    //       {defaultCurrency?.symbol} {row?.amount}
    //     </span>
    //   ),
    // },
    {
      Header: "Privacy",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.privacy?.replace("org_", "")}
        </span>
      ),
    },
    {
      Header: "Created By",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.creator ? row?.creator.name : "-"}
        </span>
      ),
    },
    {
      Header: "Created At",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.createdAt ? dayjs(row?.createdAt).format("DD MMM YYYY") : "-"}
        </span>
      ),
    },
    {
      Header: "Published",
      Cell: ({ row }) => (
        <ToggleSwitch
          checked={row?.isPublished}
          onChange={(value) => {
            if (
              !row?.isPublished &&
              !confirm(
                "Are you sure? \n You won't be able to change content after publish!",
              )
            )
              return;
            handleTogglePublish(row);
          }}
        />
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) =>
        row.creatorId === profile?._id ||
        profile?.role === "org_admin" ||
        profile?.role === "org_owner" ? (
          <div className="d-flex justify-content-start gap-2">
            <Button
              className={"icon-btn"}
              startIcon={<EyeIcon />}
              onClick={() => dispatch(selectCourse(row))}
            />
            {!row?.isPublished ? (
              <>
                <Button
                  className={"icon-btn"}
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleOpenNew(dispatch, "createCourse");
                    setEditingCourseData(row);
                  }}
                />
                <Button
                  className={"icon-btn"}
                  startIcon={<TrashIcon />}
                  onClick={() => handleDeleteCourse(row)}
                />
              </>
            ) : null}
          </div>
        ) : null,
    },
  ];

  useEffect(() => {
    return () => {
      handleDeselectCourse();
    };
  }, []);

  // Debouncing for search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebounceSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  return (
    <PageContainer>
      {selectedCourse ? (
        <CourseDetail
          selectedCourse={selectedCourse}
          setSelectedCourse={handleDeselectCourse}
        />
      ) : (
        <>
          <div className="user-table mb-3">
            <div className="user-table-top">
              <div className="mb-3 d-flex justify-content-end align-items-center">
                {/* {orgProfile?.subscriptionId?.status === "active" ? ( */}
                <Button
                  type="button"
                  className={"rounded"}
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenNew(dispatch, "createCourse")}
                >
                  Course
                </Button>
                {/* ) : null} */}
              </div>

              <DataActions
                searchInputProps={{
                  placeholder: "Search for ID, Title, Creator",
                  value: searchText,
                  onChange: (e) => setSearchText(e.target.value),
                }}
              />
            </div>

            <Table
              data={courseData}
              mapData={orgCourseMapData}
              noDataMessage="No Course Found!"
            />

            <div className="mt-3">
              <Pagination
                totalItem={totalCourses}
                activePage={page}
                pageSize={size}
                onActivePageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPage}
              />
            </div>
          </div>

          {dialogueType === "createCourse" && (
            <CreateCourse
              editingCourseData={editingCourseData}
              isEditing={editingCourseData !== null}
              onEditSuccess={handleEditSuccess}
            />
          )}
        </>
      )}
    </PageContainer>
  );
};

export default OrgCourses;
